import {
   ChronicDiseaseSimpleQuery,
   MedicalInformationCountByTypeQuery,
} from '@dreryk/egabinet-patient-medical-information-frontend-api';
import { ChronicDiseaseCannotBeRemovedWithoutReasonFault } from '@dreryk/egabinet-visit-frontend-api';

import { refetchCqrsQuery } from '@dreryk/command-query-react-common';
import { isFault, isFaultType } from '@dreryk/command-query-ws-client';
import { commonTranslationKeys, PromiseVoidFunction } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { dispatchShowRemovalChronicDiseaseModalEvent } from '../../removalChronicDisease/RemovalChronicDiseaseModal.event';
import { ChronicDiseaseRemovalParameters } from '../../removalChronicDisease/RemovalChronicDiseaseModal.types';

import { useVisitDiagnosisFaultHandlerMap, VisitDiagnosisCommonFault } from './useVisitDiagnosisFaultHandlerMap';

export const useRetryActionAfterChronicDiseaseCannotBeRemovedWithoutReasonFault = (
   action: (parameters?: ChronicDiseaseRemovalParameters) => Promise<void>
): PromiseVoidFunction => {
   const visitDiagnosisFaultHandlerMap = useVisitDiagnosisFaultHandlerMap();

   return async () =>
      new Promise<void>((resolve, reject): void => {
         const onSuccess = async (): Promise<void> => {
            await Promise.all([
               refetchCqrsQuery(ChronicDiseaseSimpleQuery),
               refetchCqrsQuery(MedicalInformationCountByTypeQuery),
            ]);
            resolve();
         };

         action()
            .then(onSuccess)
            .catch((error) => {
               if (isFaultType(error as Error, new ChronicDiseaseCannotBeRemovedWithoutReasonFault().class)) {
                  const fault = error as ChronicDiseaseCannotBeRemovedWithoutReasonFault;

                  dispatchShowRemovalChronicDiseaseModalEvent(
                     fault,
                     (parameters) => {
                        action(parameters).then(onSuccess).catch(reject);
                     },
                     reject
                  );
                  return;
               }

               if (isFault(error) && error.class in visitDiagnosisFaultHandlerMap) {
                  const visitDiagnosisCommonFault = error as VisitDiagnosisCommonFault;
                  const faultHandler = visitDiagnosisFaultHandlerMap[visitDiagnosisCommonFault.class];
                  faultHandler(visitDiagnosisCommonFault as never);

                  reject(new Error('Fault handled'));
                  return;
               }

               dispatchSnackbarNegativeEvent(commonTranslationKeys.unexpectedErrorHasOccurred);
               reject(new Error('Unexpected error has occurred'));
            });
      });
};
